import { useMemo, useState } from "react";

/**
 * PaymentLinkGenerator — Sales Hub tool for reps.
 * Type name/phone/email/business + pick a plan → get a personalized /onboard/ link.
 * Only split-2 / split-3 for now (pay-in-full is on external legacy link until Whop cap lifted).
 */
export default function PaymentLinkGenerator() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [plan, setPlan] = useState("split_3"); // "split_2" | "split_3"
  const [copied, setCopied] = useState(false);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const phoneDigits = (phone || "").replace(/\D/g, "");
  const phoneValid = phoneDigits.length >= 10;
  const canGenerate = emailValid && firstName.trim() && lastName.trim() && phoneValid && businessName.trim();

  const link = useMemo(() => {
    if (!canGenerate) return "";
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const slug = plan === "split_2" ? "split-2" : "split-3";
    const qs = new URLSearchParams({
      email: email.trim(),
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      phone: phone.trim(),
      business_name: businessName.trim(),
    }).toString();
    return `${origin.replace(/\/$/, "")}/onboard/${slug}?${qs}`;
  }, [canGenerate, plan, email, firstName, lastName, phone, businessName]);

  const copyLink = () => {
    if (!link || typeof navigator === "undefined") return;
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const reset = () => {
    setFirstName(""); setLastName(""); setEmail(""); setPhone(""); setBusinessName("");
  };

  return (
    <div style={S.page} data-testid="payment-link-gen">
      <div style={S.wrap}>
        <div style={S.eyebrow}>Sales Tool · Personalized checkout link</div>
        <h1 style={S.h1}>Generate a payment link</h1>
        <p style={S.sub}>Type in the prospect's info, pick their plan, copy the link. It opens the checkout with everything prefilled — the card iframe auto-loads.</p>

        <div style={S.grid}>
          {/* LEFT: form */}
          <form style={S.card} onSubmit={(e) => e.preventDefault()}>
            <div style={S.sectionLabel}>Prospect info</div>
            <div style={{ ...S.field, marginTop: 4 }}>
              <label style={S.label}>Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="alex@shopname.com" style={S.input} data-testid="plg-email" />
            </div>
            <div style={S.row}>
              <div style={{ ...S.field, flex: 1 }}>
                <label style={S.label}>First name</label>
                <input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Alex" style={S.input} data-testid="plg-first" />
              </div>
              <div style={{ ...S.field, flex: 1 }}>
                <label style={S.label}>Last name</label>
                <input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Jordan" style={S.input} data-testid="plg-last" />
              </div>
            </div>
            <div style={S.field}>
              <label style={S.label}>Phone</label>
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 555-5555" style={S.input} data-testid="plg-phone" />
            </div>
            <div style={S.field}>
              <label style={S.label}>Business name</label>
              <input value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="Shop name" style={S.input} data-testid="plg-business" />
            </div>

            <div style={{ ...S.sectionLabel, marginTop: 18 }}>Plan</div>
            <div style={S.planPickerRow}>
              {[
                { key: "split_2", label: "Split in 2", desc: "2 × $2,250 · 30 days apart", total: "$4,500 total" },
                { key: "split_3", label: "Split in 3", desc: "3 × $1,500 · 30 days apart", total: "$4,500 total" },
              ].map((p) => {
                const active = plan === p.key;
                return (
                  <button
                    key={p.key} type="button" onClick={() => setPlan(p.key)}
                    style={{ ...S.planCard, ...(active ? S.planCardActive : {}) }}
                    data-testid={`plg-plan-${p.key}`}
                  >
                    <div style={S.planLabel}>{p.label}</div>
                    <div style={S.planDesc}>{p.desc}</div>
                    <div style={S.planTotal}>{p.total}</div>
                  </button>
                );
              })}
            </div>
            <div style={S.note}>
              💡 Pay-in-full is on hold until Whop's per-charge cap is lifted. Send split-2 or split-3 for now.
            </div>
          </form>

          {/* RIGHT: preview + copy */}
          <div style={S.card}>
            <div style={S.sectionLabel}>Preview & copy</div>
            {!canGenerate ? (
              <div style={S.previewEmpty}>
                <div style={S.previewIcon}>🔗</div>
                <div style={S.previewEmptyTitle}>Fill all 5 fields to generate the link</div>
                <div style={S.previewEmptySub}>Missing: {[
                  !emailValid && "valid email",
                  !firstName.trim() && "first name",
                  !lastName.trim() && "last name",
                  !phoneValid && "valid phone",
                  !businessName.trim() && "business name",
                ].filter(Boolean).join(" · ")}</div>
              </div>
            ) : (
              <>
                <div style={S.previewSummary}>
                  <div><b>{firstName} {lastName}</b> · {businessName}</div>
                  <div style={S.previewMeta}>{email} · {phone}</div>
                  <div style={S.previewPill}>{plan === "split_2" ? "Split in 2" : "Split in 3"}</div>
                </div>
                <div style={S.linkBox} data-testid="plg-link-box">
                  <div style={S.linkText}>{link}</div>
                </div>
                <div style={S.actionRow}>
                  <button onClick={copyLink} style={S.primaryBtn} data-testid="plg-copy">
                    {copied ? "✓ Copied — paste it to the prospect" : "Copy link"}
                  </button>
                  <a href={link} target="_blank" rel="noopener noreferrer" style={S.secondaryBtn} data-testid="plg-open">
                    Open preview ↗
                  </a>
                </div>
                <button onClick={reset} style={S.textBtn} data-testid="plg-reset">Clear all fields</button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const S = {
  page: { minHeight: "100%", background: "#050508", color: "#fff", fontFamily: "'Manrope',system-ui,-apple-system,sans-serif", padding: "28px 20px 60px", overflow: "auto" },
  wrap: { maxWidth: 980, margin: "0 auto" },
  eyebrow: { fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 800, letterSpacing: 2, color: "#F472B6", textTransform: "uppercase", marginBottom: 10 },
  h1: { fontSize: 32, fontWeight: 800, lineHeight: 1.15, letterSpacing: "-.02em", margin: "0 0 8px", color: "#fff" },
  sub: { fontSize: 14, fontWeight: 400, color: "rgba(255,255,255,.62)", lineHeight: 1.55, margin: "0 0 24px", maxWidth: 620 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 20 },
  card: { background: "linear-gradient(180deg,rgba(255,255,255,.04),rgba(255,255,255,.02))", border: "1px solid rgba(255,255,255,.08)", borderRadius: 18, padding: "22px 20px", boxShadow: "0 14px 40px -20px rgba(0,0,0,.5)" },
  sectionLabel: { fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 800, letterSpacing: 1.6, color: "rgba(255,255,255,.55)", textTransform: "uppercase", marginBottom: 10, paddingBottom: 6, borderBottom: "1px solid rgba(255,255,255,.06)" },
  field: { marginBottom: 12, display: "flex", flexDirection: "column", gap: 6 },
  row: { display: "flex", gap: 10 },
  label: { fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 700, letterSpacing: 1.2, color: "rgba(255,255,255,.55)", textTransform: "uppercase" },
  input: { padding: "12px 14px", fontSize: 14, background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.12)", borderRadius: 10, color: "#fff", outline: "none", fontFamily: "inherit" },
  planPickerRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 4 },
  planCard: { padding: "14px 12px", background: "rgba(255,255,255,.03)", border: "1.5px solid rgba(255,255,255,.1)", borderRadius: 12, color: "rgba(255,255,255,.75)", cursor: "pointer", fontFamily: "inherit", textAlign: "left", display: "flex", flexDirection: "column", gap: 4, transition: "all .18s ease" },
  planCardActive: { background: "linear-gradient(135deg,rgba(220,50,140,.18),rgba(157,50,243,.06))", borderColor: "rgba(220,50,140,.55)", color: "#fff", boxShadow: "0 0 24px -8px rgba(220,50,140,.5)" },
  planLabel: { fontSize: 14, fontWeight: 800 },
  planDesc: { fontSize: 12, opacity: .75 },
  planTotal: { fontSize: 11, fontFamily: "'IBM Plex Mono',monospace", letterSpacing: .4, color: "#F472B6", marginTop: 2, fontWeight: 700 },
  note: { fontSize: 11.5, color: "rgba(255,255,255,.5)", marginTop: 12, padding: "10px 12px", background: "rgba(255,255,255,.02)", border: "1px dashed rgba(255,255,255,.12)", borderRadius: 8, lineHeight: 1.45 },
  previewEmpty: { padding: "40px 20px", textAlign: "center", border: "1px dashed rgba(255,255,255,.12)", borderRadius: 12, marginTop: 8 },
  previewIcon: { fontSize: 32, marginBottom: 8 },
  previewEmptyTitle: { fontSize: 14, fontWeight: 700, color: "rgba(255,255,255,.8)" },
  previewEmptySub: { fontSize: 12, color: "rgba(255,255,255,.45)", marginTop: 6, fontFamily: "'IBM Plex Mono',monospace", letterSpacing: .3 },
  previewSummary: { padding: "14px 16px", background: "linear-gradient(135deg,rgba(220,50,140,.1),rgba(157,50,243,.04))", border: "1px solid rgba(220,50,140,.28)", borderRadius: 12, marginBottom: 12 },
  previewMeta: { fontSize: 12, color: "rgba(255,255,255,.55)", marginTop: 4, fontFamily: "'IBM Plex Mono',monospace", letterSpacing: .3 },
  previewPill: { display: "inline-block", marginTop: 8, padding: "3px 10px", background: "rgba(220,50,140,.2)", border: "1px solid rgba(220,50,140,.45)", borderRadius: 100, fontSize: 11, fontWeight: 800, color: "#F472B6", letterSpacing: .5, textTransform: "uppercase", fontFamily: "'IBM Plex Mono',monospace" },
  linkBox: { padding: "12px 14px", background: "#0a0a12", border: "1px solid rgba(255,255,255,.1)", borderRadius: 10, marginBottom: 12 },
  linkText: { fontFamily: "'IBM Plex Mono',monospace", fontSize: 11, color: "#93C5FD", wordBreak: "break-all", lineHeight: 1.5 },
  actionRow: { display: "flex", gap: 8 },
  primaryBtn: { flex: 1, padding: "13px 18px", background: "linear-gradient(135deg,#dc328c 0%,#9d32f3 100%)", color: "#fff", fontSize: 14, fontWeight: 800, border: "none", borderRadius: 12, cursor: "pointer", boxShadow: "0 10px 26px -6px rgba(220,50,140,.5)", fontFamily: "inherit" },
  secondaryBtn: { padding: "13px 16px", background: "rgba(255,255,255,.05)", color: "rgba(255,255,255,.85)", fontSize: 13, fontWeight: 700, border: "1px solid rgba(255,255,255,.15)", borderRadius: 12, cursor: "pointer", textDecoration: "none", textAlign: "center", fontFamily: "inherit" },
  textBtn: { width: "100%", marginTop: 8, padding: "8px", background: "transparent", color: "rgba(255,255,255,.45)", fontSize: 12, fontWeight: 600, border: "none", cursor: "pointer", fontFamily: "inherit" },
};

if (typeof document !== "undefined" && !document.getElementById("plg-kf")) {
  const s = document.createElement("style");
  s.id = "plg-kf";
  s.textContent = "input::placeholder{color:rgba(255,255,255,.35)}input:focus{border-color:rgba(220,50,140,.55) !important}button:not(:disabled):hover{transform:translateY(-1px)}";
  document.head.appendChild(s);
}
